import type { DerivedDomain, DerivedMeter } from "./fixture/derive";
import { isActual } from "./fixture/derive";
import {
  DEMO_YEAR,
  MONTHS,
  NOW_MONTH_INDEX,
} from "./fixture/complianceGroupsFixture";
import { fmt } from "./fixture/format";
import type { ConfirmedReading } from "./useComplianceDemoStore";

/** Mirrors `ReadingsChatContext` in app/consumption/consumption_readings_chat_service.py. */
export interface ReadingsChatContext {
  country: string;
  year: number;
  cadence: DerivedDomain["cadence"];
  locations: {
    id: string;
    name: string;
    identifier: string | null;
    supplier: string | null;
    min_month_mwh: number;
    median_month_mwh: number;
    max_month_mwh: number;
    reported_through: string | null;
  }[];
}

/** One row of the backend's `ConsumptionReadingsPayload`. */
export interface ChatReading {
  location_id: string | null;
  location_name: string;
  period_start: string;
  period_end: string;
  mwh: number | null;
  basis: "meter_reading" | "invoice" | "estimate" | null;
  unit_inferred: boolean;
  source_note: string | null;
}

export interface ReadingsChatPayload {
  supplier: string | null;
  readings: ChatReading[];
  notes: string | null;
}

/** A returned reading placed against a consumption location and month, ready to review. */
export interface ReadingProposal {
  key: string;
  /** Null when no consumption location matched: shown for information, never applied. */
  meterId: string | null;
  locationName: string;
  monthIndex: number;
  period: string;
  estimate: number;
  proposed: number;
  basis: ChatReading["basis"];
  unitInferred: boolean;
  sourceNote: string | null;
}

export interface ReplacementSummary {
  domainName: string;
  count: number;
  locations: string[];
  periods: string[];
  volume: number;
  /** Proposed minus standing estimates, over the matched rows. */
  delta: number;
}

/**
 * The roster the backend prompt embeds: every effective consumption location with a three-number
 * summary of its standing monthly volumes, so the model can tell kWh from MWh without seeing all
 * twelve figures.
 */
export const buildReadingsContext = (
  domain: DerivedDomain
): ReadingsChatContext => ({
  country: domain.name,
  year: DEMO_YEAR,
  cadence: domain.cadence,
  locations: domain.meters.map((mp) => {
    const sorted = [...mp.monthly].sort((a, b) => a - b);
    const lastActual = MONTHS.map((_, i) => i)
      .filter((i) => isActual(mp, i))
      .pop();
    return {
      id: mp.id,
      name: mp.name,
      identifier: mp.mpid ?? (mp.added ? null : mp.id),
      supplier: mp.supplier || null,
      min_month_mwh: sorted[0] ?? 0,
      median_month_mwh: sorted[Math.floor(sorted.length / 2)] ?? 0,
      max_month_mwh: sorted[sorted.length - 1] ?? 0,
      reported_through:
        lastActual === undefined ? null : `${MONTHS[lastActual]} ${DEMO_YEAR}`,
    };
  }),
});

/**
 * Place the assistant's rows against the domain's consumption locations, one proposal per
 * location-month. Rows spanning several months are split evenly over the whole span; the shares
 * for months that have not elapsed, or that fall outside the demo year, are dropped rather than
 * folded into the remaining months (a row with nothing left comes back as `skipped`).
 */
export const mapReadingsToProposals = (
  domain: DerivedDomain,
  payload: ReadingsChatPayload
): { proposals: ReadingProposal[]; skipped: number } => {
  const proposals: ReadingProposal[] = [];
  let skipped = 0;
  payload.readings.forEach((row, index) => {
    if (row.mwh === null) {
      skipped += 1;
      return;
    }
    const slots = monthsCovered(row);
    if (!slots.some((monthIndex) => monthIndex !== null)) {
      skipped += 1;
      return;
    }
    const meter = matchMeter(domain.meters, row);
    const shares = splitWhole(row.mwh, slots.length);
    slots.forEach((monthIndex, position) => {
      if (monthIndex === null) return;
      proposals.push({
        key: `${index}-${monthIndex}`,
        meterId: meter?.id ?? null,
        locationName: meter?.name ?? row.location_name,
        monthIndex,
        period: `${MONTHS[monthIndex]} ${DEMO_YEAR}`,
        estimate: meter?.monthly[monthIndex] ?? 0,
        proposed: shares[position],
        basis: row.basis,
        unitInferred: row.unit_inferred,
        sourceNote:
          slots.length > 1
            ? `${row.source_note ?? "Document"} · split evenly over ${slots.length} months`
            : row.source_note,
      });
    });
  });
  return { proposals: collapseDuplicates(proposals), skipped };
};

export const summarizeProposals = (
  domain: DerivedDomain,
  proposals: ReadingProposal[]
): ReplacementSummary => {
  const matched = proposals.filter((p) => p.meterId !== null);
  return {
    domainName: domain.name,
    count: matched.length,
    locations: [...new Set(matched.map((p) => p.locationName))],
    periods: [
      ...new Set(
        [...matched]
          .sort((a, b) => a.monthIndex - b.monthIndex)
          .map((p) => p.period)
      ),
    ],
    volume: matched.reduce((total, p) => total + p.proposed, 0),
    delta: matched.reduce((total, p) => total + p.proposed - p.estimate, 0),
  };
};

export const toConfirmedReadings = (
  proposals: ReadingProposal[]
): ConfirmedReading[] =>
  proposals
    .filter(
      (p): p is ReadingProposal & { meterId: string } => p.meterId !== null
    )
    .map((p) => ({ meterId: p.meterId, month: p.monthIndex, mwh: p.proposed }));

/** "+1 200 MWh" / "−300 MWh" / "No change". */
export const describeDelta = (delta: number): string =>
  delta === 0
    ? "No change"
    : `${delta > 0 ? "+" : "−"}${fmt(Math.abs(delta))} MWh`;

/**
 * One slot per month of the period the row covers, in order: the demo-year month index when that
 * month can take a reading, `null` when it has not elapsed or lies outside the demo year. The
 * volume is divided over every slot, never over the usable ones alone, so a yearly or quarterly
 * figure is not compressed into the months that have already passed.
 */
const monthsCovered = (row: ChatReading): (number | null)[] => {
  const start = parseIsoDate(row.period_start);
  const end = parseIsoDate(row.period_end);
  // Both boundaries must be real calendar dates in order; a malformed period is skipped, never
  // guessed at, because a confirmed reading replaces an estimate for good.
  if (!start || !end || compareMonths(start, end) > 0) return [];
  const slots: (number | null)[] = [];
  for (
    let year = start.year, month = start.month;
    year < end.year || (year === end.year && month <= end.month);
  ) {
    slots.push(year === DEMO_YEAR && month < NOW_MONTH_INDEX ? month : null);
    month += 1;
    if (month === 12) {
      month = 0;
      year += 1;
    }
  }
  return slots;
};

/**
 * Whole-MWh shares that add back up to the rounded total; the earliest months carry the
 * remainder. Readings are stored as whole MWh, so per-month rounding must not lose volume.
 */
const splitWhole = (total: number, parts: number): number[] => {
  const whole = Math.round(total);
  const base = Math.floor(whole / parts);
  const remainder = whole - base * parts;
  return Array.from(
    { length: parts },
    (_, i) => base + (i < remainder ? 1 : 0)
  );
};

/**
 * The store keeps one value per location-month and the last write wins, so the review table must
 * show the same: a later row for an already-proposed location-month replaces the earlier one in
 * place. Unmatched rows are never merged, as nothing identifies them as the same location.
 */
const collapseDuplicates = (
  proposals: ReadingProposal[]
): ReadingProposal[] => {
  const byCell = new Map<string, ReadingProposal>();
  proposals.forEach((p) =>
    byCell.set(
      p.meterId === null
        ? `unmatched:${p.key}`
        : `${p.meterId}:${p.monthIndex}`,
      p
    )
  );
  return [...byCell.values()];
};

type YearMonth = { year: number; month: number };

/** Accepts only a full `YYYY-MM-DD` that names an existing day (no 2026-02-99, no 2026-13-01). */
const parseIsoDate = (value: string | null): YearMonth | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  const [year, month, day] = [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
  ];
  const date = new Date(Date.UTC(year, month - 1, day));
  const isRealDay =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  return isRealDay ? { year, month: month - 1 } : null;
};

const compareMonths = (a: YearMonth, b: YearMonth): number =>
  a.year !== b.year ? a.year - b.year : a.month - b.month;

const normalize = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

// The roster hands the model `id`, `name` and `identifier` (the metering point id, or the id
// again when there is none), and the model may echo any of them back in either column.
const matchMeter = (
  meters: DerivedMeter[],
  row: ChatReading
): DerivedMeter | undefined => {
  const handles = [row.location_id, row.location_name]
    .map(normalize)
    .filter(Boolean);
  const matches = (value: string | null | undefined) =>
    !!value && handles.includes(normalize(value));
  return (
    meters.find((mp) => matches(mp.id)) ??
    meters.find((mp) => matches(mp.mpid)) ??
    meters.find((mp) => matches(mp.name))
  );
};
